import { useContext } from "react";
import { Redirect } from "react-router";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { type ILeadPageRedirectState, leadDetailRoute } from "utilities/flatRoutes/lead.routes";
import { lazyLoadWithRetry } from "utilities/react.utilities";
import { routes } from "utilities/routes";
import { isDeepLinkEntry } from "utilities/routing/isDeepLinkEntry";

import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";

import { type ILeadProps } from "entity/lead/Lead/Lead";

const Lead = lazyLoadWithRetry(() => import("./Lead"));

export const RouteLead = (props: Omit<ILeadProps, "leadId" | "history" | "match" | "location">) => {
    const builderInfo = useContext(BuilderInfoContext);

    return (
        <RouteRelative
            path={routes.lead.details}
            render={(routeProps) => {
                const leadId = parseInt(routeProps.match.params.id);

                // leadsFullPageConversion: send the legacy nested modal URL (`<list>/Lead/:id[/LeadActivity/...]`)
                // to the lead page. This `RouteRelative` match is non-exact, so a trailing child modal path
                // lives in the unmatched tail and is carried over to open on top of the page. The page is
                // a top-level route, so it never matches this nested route again.
                if (builderInfo?.flags.leadsFullPageConversion && !Number.isNaN(leadId)) {
                    const subPath = routeProps.location.pathname.slice(routeProps.match.url.length);
                    const deepLinkState: ILeadPageRedirectState | undefined = isDeepLinkEntry(
                        routeProps.history,
                        routeProps.location,
                    )
                        ? { openedByDeepLink: true }
                        : undefined;
                    return (
                        <Redirect
                            to={{
                                pathname:
                                    (leadDetailRoute.link({ leadId }, undefined, {
                                        usePrevious: false,
                                    }) as string) + subPath,
                                search: routeProps.location.search,
                                // Keeps `edit`, which the activity modal needs to open editable. A deep
                                // link is the browser's initial entry, which never carries state.
                                state: deepLinkState ?? routeProps.location.state,
                            }}
                        />
                    );
                }

                return (
                    <BTSuspense>
                        <Lead {...props} {...routeProps} leadId={leadId ?? -1} />
                    </BTSuspense>
                );
            }}
        />
    );
};
